import type { BoardData, BuiltinLayerKey, FreeformElement } from '../model/schema.js';

export type WorkspaceRole = 'owner' | 'admin' | 'editor' | 'viewer';
export type LinkAccess = 'view' | 'comment' | 'edit';
export type LinkState = 'active' | 'expired' | 'revoked';

const ROLE_RANK: Record<WorkspaceRole, number> = { viewer: 0, editor: 1, admin: 2, owner: 3 };

export function roleAtLeast(role: WorkspaceRole, min: WorkspaceRole): boolean {
  return ROLE_RANK[role] >= ROLE_RANK[min];
}

export type MemberAction =
  | 'board.read'
  | 'board.edit'
  | 'board.create'
  | 'board.delete'
  | 'board.restore'
  | 'board.replace'
  | 'snapshot.create'
  | 'snapshot.restore'
  | 'release.create'
  | 'release.signoff'
  | 'release.settings'
  | 'meeting.manage'
  | 'link.manage'
  | 'comment.write'
  | 'comment.moderate'
  | 'member.manage'
  | 'workspace.manage'
  | 'workspace.delete'
  | 'catalog.edit'
  | 'inventory.edit'
  | 'template.edit';

const ACTION_MIN_ROLE: Record<MemberAction, WorkspaceRole> = {
  'board.read': 'viewer',
  'comment.write': 'viewer',
  'board.edit': 'editor',
  'board.create': 'editor',
  'snapshot.create': 'editor',
  // released versions (U80): members release, everyone signs, the owner requires signatures
  'release.create': 'editor',
  'release.signoff': 'viewer',
  'release.settings': 'owner',
  // meetings (U85): editors invite, change and cancel
  'meeting.manage': 'editor',
  'catalog.edit': 'editor',
  'inventory.edit': 'editor',
  'template.edit': 'editor',
  'board.delete': 'admin',
  'board.restore': 'admin',
  // replace the content from a portable file (U93): admins, and the board's creator (boardMemberCan)
  'board.replace': 'admin',
  'snapshot.restore': 'admin',
  'link.manage': 'admin',
  'comment.moderate': 'admin',
  'member.manage': 'admin',
  'workspace.manage': 'admin',
  'workspace.delete': 'owner',
};

export function memberCan(role: WorkspaceRole, action: MemberAction): boolean {
  return roleAtLeast(role, ACTION_MIN_ROLE[action]);
}

/** Actions of the board owner: its creator (while still allowed to edit) besides admins / owners. */
const BOARD_OWNER_ACTIONS: ReadonlySet<MemberAction> = new Set(['board.replace']);

/**
 * {@link memberCan} for one board: `isCreator` = the member created it (`boards.created_by`).
 * Board-owner actions (U93) are also granted to the creator when the role may edit.
 */
export function boardMemberCan(
  role: WorkspaceRole,
  isCreator: boolean,
  action: MemberAction,
): boolean {
  if (memberCan(role, action)) return true;
  return isCreator && BOARD_OWNER_ACTIONS.has(action) && memberCan(role, 'board.edit');
}

export interface LinkLike {
  access: LinkAccess;
  expiresAt: string | Date;
  revokedAt?: string | Date | null;
}

/** Link state machine (decision Q6). Revocation wins over expiry. */
export function linkState(link: LinkLike, now: Date = new Date()): LinkState {
  if (link.revokedAt) return 'revoked';
  return new Date(link.expiresAt).getTime() <= now.getTime() ? 'expired' : 'active';
}

/** Extending re-activates a link from any state: same token, guest data kept. */
export function extendLink<L extends LinkLike>(
  link: L,
  newExpiry: Date,
  now: Date = new Date(),
): L {
  if (newExpiry.getTime() <= now.getTime()) {
    throw new RangeError('New expiry must be in the future');
  }
  return { ...link, expiresAt: newExpiry.toISOString(), revokedAt: null };
}

export function revokeLink<L extends LinkLike>(link: L, now: Date = new Date()): L {
  return { ...link, revokedAt: now.toISOString() };
}

export const DEFAULT_LINK_LIFETIME_DAYS = 30;

export function defaultLinkExpiry(now: Date = new Date()): Date {
  return new Date(now.getTime() + DEFAULT_LINK_LIFETIME_DAYS * 86_400_000);
}

/** Layer restriction is only allowed for read-only style links (edit guests need the full live doc). */
export function layerRestrictionAllowed(access: LinkAccess): boolean {
  return access !== 'edit';
}

export type Principal =
  | { kind: 'member'; userId: string; role: WorkspaceRole }
  | { kind: 'guest'; guestId: string; linkId: string; access: LinkAccess };

export type DocMode = 'none' | 'projection' | 'read' | 'write';

/**
 * How a principal may access the main board document (architecture §4).
 * `holdsLease` = principal currently holds the soft edit lock.
 */
export function mainDocMode(p: Principal, holdsLease: boolean): DocMode {
  if (p.kind === 'member') {
    if (!memberCan(p.role, 'board.edit')) return 'read';
    return holdsLease ? 'write' : 'read';
  }
  if (p.access === 'edit') return holdsLease ? 'write' : 'read';
  return 'projection';
}

/** Access to the overlay document belonging to `linkId`. */
export function overlayDocMode(p: Principal, linkId: string): DocMode {
  if (p.kind === 'member') return memberCan(p.role, 'board.edit') ? 'write' : 'read';
  if (p.linkId !== linkId) return 'none';
  return p.access === 'view' ? 'read' : 'write';
}

export function canComment(p: Principal): boolean {
  return p.kind === 'member' || p.access !== 'view';
}

export function canEditBoard(p: Principal): boolean {
  return p.kind === 'member' ? memberCan(p.role, 'board.edit') : p.access === 'edit';
}

/** Comments are scoped per link (decision Q4): members see all, guests only their link's. */
export function canSeeComment(p: Principal, comment: { linkId: string | null }): boolean {
  return p.kind === 'member' || comment.linkId === p.linkId;
}

/**
 * Decision U6 (trusted circle, no permission scheme on board content): everyone who can see a
 * comment and may comment can edit, resolve and delete it. Only view guests are read-only.
 */
export function canModifyComment(p: Principal, comment: { linkId: string | null }): boolean {
  return canSeeComment(p, comment) && canComment(p);
}

/**
 * Server-side projection for guests with a layer restriction: removes everything on layers
 * outside the allowed set. References to removed entities are stripped so the projection has no
 * dangling ids: channel sources, mix outputs, sub-core `via`, power assignments, power-source
 * items, stand mounts, parents and arrow endpoints. Connections are kept only if both ends survive.
 * Ink strokes survive on allowed layers only (built-in key `ink`), attached ones only with their element.
 */
export function filterByLayers(
  data: BoardData,
  allowed: readonly BuiltinLayerKey[] | null,
): BoardData {
  if (allowed === null) return data;
  const allowedLayerIds = new Set(
    Object.values(data.layers)
      .filter((l) => l.key !== undefined && allowed.includes(l.key))
      .map((l) => l.id),
  );
  const keptItems = Object.entries(data.items).filter(([, i]) => allowedLayerIds.has(i.layerId));
  const keptItemIds = new Set(keptItems.map(([id]) => id));
  const keep = (id: string | undefined): boolean => id !== undefined && keptItemIds.has(id);
  const power = Object.fromEntries(
    Object.entries(allowed.includes('power') ? data.power : {}).map(([id, p]) => {
      if (p.itemId === undefined || keep(p.itemId)) return [id, p];
      const { itemId: _i, ...rest } = p;
      return [id, rest];
    }),
  );
  const items = Object.fromEntries(
    keptItems.map(([id, i]) => {
      const out = { ...i };
      if (i.power?.sourceId !== undefined && !(i.power.sourceId in power)) {
        const { sourceId: _s, ...rest } = i.power;
        out.power = rest;
      }
      if (i.mountedOn !== undefined && !keep(i.mountedOn)) delete out.mountedOn;
      if (i.parentId !== undefined && !keep(i.parentId)) delete out.parentId;
      return [id, out];
    }),
  );
  const layers = Object.fromEntries(
    Object.entries(data.layers).filter(([id]) => allowedLayerIds.has(id)),
  );
  const connections = Object.fromEntries(
    Object.entries(data.connections)
      .filter(([, c]) => keep(c.from.itemId) && keep(c.to.itemId))
      .map(([id, c]) => {
        if (c.via === undefined || keep(c.via.itemId)) return [id, c];
        const { via: _v, ...rest } = c;
        return [id, rest];
      }),
  );
  const channels = Object.fromEntries(
    Object.entries(data.channels).map(([id, c]) => {
      if (c.sourceItemId === undefined || keep(c.sourceItemId)) return [id, c];
      const { sourceItemId: _s, sourcePortId: _p, ...rest } = c;
      return [id, rest];
    }),
  );
  const mixes = Object.fromEntries(
    Object.entries(data.mixes).map(([id, m]) => [
      id,
      { ...m, outputItemIds: m.outputItemIds.filter((o) => keep(o)) },
    ]),
  );
  const keptFreeform = Object.entries(data.freeform).filter(
    ([, f]) => f.layerId === undefined || allowedLayerIds.has(f.layerId),
  );
  const keptIds = new Set(keptFreeform.map(([id]) => id));
  const dangling = (e: FreeformElement['from']): boolean =>
    e !== undefined && 'elementId' in e && !keptIds.has(e.elementId);
  const freeform = Object.fromEntries(
    keptFreeform.map(([id, f]) => {
      const out = { ...f };
      if (dangling(f.from)) delete out.from;
      if (dangling(f.to)) delete out.to;
      return [id, out];
    }),
  );
  // Ink only on allowed (built-in) layers; attached ink disappears with its element.
  const ink = Object.fromEntries(
    Object.entries(data.ink).filter(
      ([, s]) =>
        allowedLayerIds.has(s.layerId) && (s.attachedTo === undefined || keptIds.has(s.attachedTo)),
    ),
  );
  return { ...data, layers, items, connections, channels, mixes, power, freeform, ink };
}
